import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Select } from "../ui/select";
import { Badge } from "../ui/badge";
import { Edit3, Tag, CheckCircle, X, AlertTriangle, Loader2, Trash2, Globe, MapPin } from "lucide-react";
import { toast } from "sonner";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { fetchDesks } from "../../services/crmServices/deskService";
import { searchUsers } from "../../services/crmServices/userService";
import { bulkUpdateLeads, deleteLead, fetchDistinctValues } from "../../services/crmServices/leadService";
import { getFullName } from "../../services/common/util";
import { useAuth } from "../../hooks/useAuth";
import { Country } from "country-state-city";

export function BulkEditModal({ open, onClose, selectedLeads, onSuccess }) {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const [loading, setLoading] = useState(false);
    const [desks, setDesks] = useState([]);
    const [agents, setAgents] = useState([]);
    const [selectedDesk, setSelectedDesk] = useState("");
    const [sources, setSources] = useState([]);
    const [countries, setCountries] = useState([]);
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0, percentage: 0 });

    const [bulkActions, setBulkActions] = useState({
        changeStatus: false,
        changeSource: false,
        changeCountry: false,
        deleteLeads: false
    });

    const [actionValues, setActionValues] = useState({
        newStatus: "",
        newAgent: "",
        newDesk: "",
        newSource: "",
        newCountry: ""
    });

    useEffect(() => {
        if (open) {
            fetchDesks().then((res) => {
                if (res?.data?.length) {
                    setDesks(res.data);
                }
            });
            
            fetchDistinctValues('source').then((res) => {
                if (res?.data) {
                    setSources(res.data.map((source) => ({ label: source, value: source })));
                }
            });

            const loadCountries = async () => {
                try {
                    const countriesResponse = await fetchDistinctValues('country');
                    if (countriesResponse?.data && countriesResponse.data.length > 0) {
                        const dbCountries = countriesResponse.data.map((country) => ({
                            label: country,
                            value: country
                        }));
                        
                        const libraryCountries = Country.getAllCountries().map((country) => ({
                            label: country.name,
                            value: country.name
                        }));
                        
                        const countryMap = new Map();
                        dbCountries.forEach(country => {
                            countryMap.set(country.value, country);
                        });
                        libraryCountries.forEach(country => {
                            if (!countryMap.has(country.value)) {
                                countryMap.set(country.value, country);
                            }
                        });
                        
                        const combinedCountries = Array.from(countryMap.values()).sort((a, b) => 
                            a.label.localeCompare(b.label)
                        );
                        
                        setCountries(combinedCountries);
                    } else {
                        const libraryCountries = Country.getAllCountries().map((country) => ({
                            label: country.name,
                            value: country.name
                        }));
                        setCountries(libraryCountries);
                    }
                } catch (error) {
                    console.error("Error fetching countries:", error);
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    setCountries(libraryCountries);
                }
            };
            
            loadCountries();
        }
    }, [open]);

    useEffect(() => {
        if (selectedDesk) {
            searchUsers({ agentDesk: selectedDesk }).then((res) => {
                if (res?.data) {
                    setAgents(res.data);
                } else {
                    setAgents([]);
                }
            });
        } else {
            setAgents([]);
        }
    }, [selectedDesk]);

    const handleActionToggle = (action) => {
        if (action === "deleteLeads" && !bulkActions.deleteLeads) {
            setBulkActions({
                changeStatus: false,
                changeSource: false,
                changeCountry: false,
                deleteLeads: true
            });
            setActionValues({
                newStatus: "",
                newAgent: "",
                newDesk: "",
                newSource: "",
                newCountry: ""
            });
            setSelectedDesk("");
            return;
        }

        if (action !== "deleteLeads" && bulkActions.deleteLeads) {
            setBulkActions((prev) => ({
                ...prev,
                deleteLeads: false,
                [action]: !prev[action]
            }));
            return;
        }

        setBulkActions((prev) => ({
            ...prev,
            [action]: !prev[action]
        }));

        if (bulkActions[action]) {
            let valueKey = "";
            if (action === "changeStatus") {
                valueKey = "newStatus";
            } else if (action === "changeSource") {
                valueKey = "newSource";
            } else if (action === "changeCountry") {
                valueKey = "newCountry";
            }

            setActionValues((prev) => ({
                ...prev,
                ...(valueKey && { [valueKey]: "" })
            }));
        }
    };

    const handleValueChange = (key, value) => {
        setActionValues((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const validateActions = () => {
        if (bulkActions.deleteLeads) {
            return true;
        }

        if (!bulkActions.changeStatus && !bulkActions.changeSource && !bulkActions.changeCountry) {
            toast.error("Please select at least one action to perform");
            return false;
        }

        if (bulkActions.changeStatus && !actionValues.newStatus) {
            toast.error("Please select a status to change to");
            return false;
        }

        if (bulkActions.changeSource && !actionValues.newSource) {
            toast.error("Please enter or select a source name");
            return false;
        }

        if (bulkActions.changeCountry && !actionValues.newCountry) {
            toast.error("Please select a country");
            return false;
        }

        return true;
    };

    const handleBulkDelete = async () => {
        const MAX_DELETE_LIMIT = 15000;
        if (selectedLeads.length > MAX_DELETE_LIMIT) {
            toast.error(`Cannot delete more than ${MAX_DELETE_LIMIT} leads at once. Please select fewer leads or split into smaller batches.`);
            return;
        }

        setLoading(true);
        let deletedCount = 0;
        let errorCount = 0;
        const totalLeads = selectedLeads.length;

        setDeleteProgress({ current: 0, total: totalLeads, percentage: 0 });

        const progressToast = toast.loading(`Deleting leads... 0 / ${totalLeads} (0%)`);

        try {
            for (let i = 0; i < selectedLeads.length; i++) {
                const lead = selectedLeads[i];
                try {
                    const response = await deleteLead({ _id: lead._id });
                    if (response?.status === 200) {
                        deletedCount++;
                    } else {
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error deleting lead ${lead._id}:`, error);
                    errorCount++;
                }

                const processed = i + 1;
                const percentage = Math.round((processed / totalLeads) * 100);
                setDeleteProgress({
                    current: processed,
                    total: totalLeads,
                    percentage: percentage
                });
                toast.loading(
                    `Deleting leads... ${processed} / ${totalLeads} (${percentage}%)`,
                    { id: progressToast }
                );
            }

            toast.dismiss(progressToast);
            setDeleteProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (deletedCount > 0) {
                toast.success(`Successfully deleted ${deletedCount} lead(s)!`);
                if (errorCount > 0) {
                    toast.warning(`${errorCount} lead(s) could not be deleted`);
                }
                onSuccess();
                handleClose();
            } else {
                toast.error("Failed to delete leads");
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Bulk delete error:", error);
            toast.error("Failed to delete leads");
        } finally {
            setLoading(false);
            setDeleteProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleBulkUpdate = async () => {
        if (!validateActions()) return;

        if (bulkActions.deleteLeads) {
            Swal.fire({
                title: "Are you sure?",
                text: `You are about to delete ${selectedLeads.length} lead(s). This action cannot be undone!`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete them!",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#dc2626",
                cancelButtonColor: "#6b7280"
            }).then((result) => {
                if (result.isConfirmed) {
                    handleBulkDelete();
                }
            });
            return;
        }

        setLoading(true);
        const startTime = Date.now();
        const totalLeads = selectedLeads.length;
        setProgress({ current: 0, total: totalLeads, percentage: 0 });

        const progressToast = toast.loading(`Preparing to update ${totalLeads.toLocaleString()} leads...`);

        try {
            const updateData = {
                leadIds: selectedLeads.map((lead) => lead._id),
                updates: {}
            };

            if (bulkActions.changeStatus) {
                updateData.updates.status = actionValues.newStatus;
            }

            if (bulkActions.changeSource) {
                updateData.updates.source = actionValues.newSource;
            }

            if (bulkActions.changeCountry) {
                updateData.updates.country = actionValues.newCountry;
            }

            let progressStep = 0;
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    progressStep++;
                    const progressValue = Math.min(95, Math.round(100 * (1 - Math.exp(-progressStep / 8))));
                    const currentCount = Math.round((progressValue / 100) * totalLeads);
                    const elapsed = Math.round((Date.now() - startTime) / 1000);
                    const estimated = progressValue > 5 ? Math.round((elapsed / progressValue) * 100) : 0;
                    const remaining = Math.max(0, estimated - elapsed);
                    
                    toast.loading(
                        `Updating ${currentCount.toLocaleString()} / ${totalLeads.toLocaleString()} leads (${progressValue}%) • ${remaining}s remaining`,
                        { id: progressToast }
                    );
                    return { current: currentCount, total: totalLeads, percentage: progressValue };
                });
            }, 300);

            const response = await bulkUpdateLeads(updateData);

            clearInterval(progressInterval);
            const elapsed = Math.round((Date.now() - startTime) / 1000);
            toast.dismiss(progressToast);
            setProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (response?.status === 200) {
                const actualCount = response?.data?.count || selectedLeads.length;
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                toast.success(`✓ Successfully updated ${actualCount.toLocaleString()} leads in ${timeText}`, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                toast.error(response?.data?.message || "Failed to update leads");
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Bulk update error:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to update leads";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleClose = () => {
        setBulkActions({
            changeStatus: false,
            changeSource: false,
            changeCountry: false,
            deleteLeads: false
        });
        setActionValues({
            newStatus: "",
            newAgent: "",
            newDesk: "",
            newSource: "",
            newCountry: ""
        });
        setSelectedDesk("");
        setDeleteProgress({ current: 0, total: 0, percentage: 0 });
        onClose();
    };

    const getActionSummary = () => {
        const actions = [];
        if (bulkActions.changeStatus) {
            const statusLabel = leadStatuses.find((s) => s.value === actionValues.newStatus)?.label;
            actions.push(`Change status to "${statusLabel}"`);
        }
        if (bulkActions.changeSource) {
            actions.push(`Change source to "${actionValues.newSource}"`);
        }
        if (bulkActions.changeCountry) {
            actions.push(`Change country to "${actionValues.newCountry}"`);
        }
        if (bulkActions.deleteLeads) {
            actions.push(`Delete ${selectedLeads.length} lead(s)`);
        }
        return actions;
    };

    const activeActionCount = Object.values(bulkActions).filter(Boolean).length;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl max-h-[85vh] flex flex-col p-0 gap-0 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 bg-gradient-to-r from-slate-50 to-white border-b">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600">
                            <Edit3 className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <DialogHeader className="p-0 space-y-0.5">
                                <DialogTitle className="text-lg font-semibold text-gray-900">
                                    Edit Leads
                                </DialogTitle>
                                <DialogDescription className="text-sm text-gray-500">
                                    Apply changes to {selectedLeads.length} selected lead{selectedLeads.length !== 1 ? "s" : ""}
                                </DialogDescription>
                            </DialogHeader>
                        </div>
                        <Badge variant="secondary" className="text-xs font-medium">
                            {selectedLeads.length} selected
                        </Badge>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    {/* Action Options */}
                    <div className="space-y-3">
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Select Actions</p>

                        {/* Change Status */}
                        <div
                            onClick={() => !bulkActions.deleteLeads && handleActionToggle("changeStatus")}
                            className={`rounded-lg border transition-all cursor-pointer ${
                                bulkActions.changeStatus
                                    ? "border-blue-200 bg-blue-50/50 shadow-sm"
                                    : bulkActions.deleteLeads
                                    ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                            }`}
                        >
                            <div className="flex items-center gap-3 p-3.5">
                                <div className={`flex items-center justify-center w-8 h-8 rounded-md ${
                                    bulkActions.changeStatus ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-400"
                                }`}>
                                    <Tag className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">Change Status</p>
                                    <p className="text-xs text-gray-500">Update lead status for all selected</p>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={bulkActions.changeStatus}
                                    onChange={() => {}}
                                    disabled={bulkActions.deleteLeads}
                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            {bulkActions.changeStatus && (
                                <div className="px-3.5 pb-3.5 pt-0" onClick={(e) => e.stopPropagation()}>
                                    <Select
                                        options={leadStatuses}
                                        value={actionValues.newStatus}
                                        onChange={(value) => handleValueChange("newStatus", value)}
                                        placeholder="Select new status"
                                        searchable
                                    />
                                </div>
                            )}
                        </div>

                        {/* Change Source */}
                        <div
                            onClick={() => !bulkActions.deleteLeads && handleActionToggle("changeSource")}
                            className={`rounded-lg border transition-all cursor-pointer ${
                                bulkActions.changeSource
                                    ? "border-emerald-200 bg-emerald-50/50 shadow-sm"
                                    : bulkActions.deleteLeads
                                    ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                            }`}
                        >
                            <div className="flex items-center gap-3 p-3.5">
                                <div className={`flex items-center justify-center w-8 h-8 rounded-md ${
                                    bulkActions.changeSource ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-400"
                                }`}>
                                    <Globe className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">Change Source</p>
                                    <p className="text-xs text-gray-500">Update the source field</p>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={bulkActions.changeSource}
                                    onChange={() => {}}
                                    disabled={bulkActions.deleteLeads}
                                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            {bulkActions.changeSource && (
                                <div className="px-3.5 pb-3.5 pt-0 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                                    <Input
                                        type="text"
                                        value={actionValues.newSource}
                                        onChange={(e) => handleValueChange("newSource", e.target.value)}
                                        placeholder="Enter source name"
                                        list="source-options"
                                    />
                                    {sources.length > 0 && (
                                        <datalist id="source-options">
                                            {sources.map((source) => (
                                                <option key={source.value} value={source.value} />
                                            ))}
                                        </datalist>
                                    )}
                                    {sources.length > 0 && (
                                        <p className="text-xs text-gray-400">
                                            Existing: {sources.slice(0, 4).map(s => s.label).join(", ")}
                                            {sources.length > 4 && ` +${sources.length - 4} more`}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Change Country */}
                        <div
                            onClick={() => !bulkActions.deleteLeads && handleActionToggle("changeCountry")}
                            className={`rounded-lg border transition-all cursor-pointer ${
                                bulkActions.changeCountry
                                    ? "border-amber-200 bg-amber-50/50 shadow-sm"
                                    : bulkActions.deleteLeads
                                    ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                            }`}
                        >
                            <div className="flex items-center gap-3 p-3.5">
                                <div className={`flex items-center justify-center w-8 h-8 rounded-md ${
                                    bulkActions.changeCountry ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-400"
                                }`}>
                                    <MapPin className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">Change Country</p>
                                    <p className="text-xs text-gray-500">Update the country field</p>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={bulkActions.changeCountry}
                                    onChange={() => {}}
                                    disabled={bulkActions.deleteLeads}
                                    className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            {bulkActions.changeCountry && (
                                <div className="px-3.5 pb-3.5 pt-0" onClick={(e) => e.stopPropagation()}>
                                    <Select
                                        options={countries}
                                        value={actionValues.newCountry}
                                        onChange={(value) => handleValueChange("newCountry", value)}
                                        placeholder="Select country"
                                        searchable
                                    />
                                </div>
                            )}
                        </div>

                        {/* Delete Leads */}
                        {user.permissions?.leads?.delete && (
                            <div
                                onClick={() => handleActionToggle("deleteLeads")}
                                className={`rounded-lg border transition-all cursor-pointer ${
                                    bulkActions.deleteLeads
                                        ? "border-red-300 bg-red-50 shadow-sm"
                                        : "border-gray-200 hover:border-red-200 hover:bg-red-50/30"
                                }`}
                            >
                                <div className="flex items-center gap-3 p-3.5">
                                    <div className={`flex items-center justify-center w-8 h-8 rounded-md ${
                                        bulkActions.deleteLeads ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-400"
                                    }`}>
                                        <Trash2 className="h-4 w-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium ${bulkActions.deleteLeads ? "text-red-700" : "text-gray-900"}`}>
                                            Delete Leads
                                        </p>
                                        <p className="text-xs text-gray-500">Permanently remove selected leads</p>
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={bulkActions.deleteLeads}
                                        onChange={() => {}}
                                        className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                                {bulkActions.deleteLeads && (
                                    <div className="mx-3.5 mb-3.5 p-3 bg-red-100/60 rounded-md border border-red-200">
                                        <p className="text-xs text-red-700 font-medium">
                                            ⚠ This will permanently delete {selectedLeads.length} lead(s). This cannot be undone.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Progress Bar - Update */}
                    {loading && !bulkActions.deleteLeads && progress.total > 0 && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50/70 p-4">
                            <div className="flex items-center justify-between mb-2.5">
                                <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                                    <span className="text-sm font-medium text-blue-800">Updating leads...</span>
                                </div>
                                <span className="text-sm font-semibold text-blue-700">{progress.percentage}%</span>
                            </div>
                            <div className="w-full bg-blue-200/60 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progress.percentage}%` }}
                                />
                            </div>
                            <p className="text-xs text-blue-600 mt-1.5">
                                {progress.current.toLocaleString()} / {progress.total.toLocaleString()} leads processed
                            </p>
                        </div>
                    )}

                    {/* Progress Bar - Delete */}
                    {loading && bulkActions.deleteLeads && deleteProgress.total > 0 && (
                        <div className="rounded-lg border border-red-200 bg-red-50/70 p-4">
                            <div className="flex items-center justify-between mb-2.5">
                                <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 text-red-600 animate-spin" />
                                    <span className="text-sm font-medium text-red-800">Deleting leads...</span>
                                </div>
                                <span className="text-sm font-semibold text-red-700">{deleteProgress.percentage}%</span>
                            </div>
                            <div className="w-full bg-red-200/60 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-red-600 h-2 rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${deleteProgress.percentage}%` }}
                                />
                            </div>
                            <p className="text-xs text-red-600 mt-1.5">
                                {deleteProgress.current.toLocaleString()} / {deleteProgress.total.toLocaleString()} leads deleted
                            </p>
                        </div>
                    )}

                    {/* Action Summary */}
                    {getActionSummary().length > 0 && !loading && (
                        <div className={`rounded-lg border p-4 ${
                            bulkActions.deleteLeads ? "border-red-200 bg-red-50/50" : "border-blue-200 bg-blue-50/50"
                        }`}>
                            <p className={`text-xs font-medium uppercase tracking-wider mb-2 ${
                                bulkActions.deleteLeads ? "text-red-500" : "text-blue-500"
                            }`}>
                                Summary
                            </p>
                            <ul className="space-y-1">
                                {getActionSummary().map((action, index) => (
                                    <li key={index} className="flex items-center gap-2 text-sm text-gray-700">
                                        <CheckCircle className={`h-3.5 w-3.5 flex-shrink-0 ${
                                            bulkActions.deleteLeads ? "text-red-500" : "text-blue-500"
                                        }`} />
                                        {action}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Warning */}
                    {!bulkActions.deleteLeads && activeActionCount > 0 && !loading && (
                        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50/70 border border-amber-200">
                            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-700 leading-relaxed">
                                This action cannot be undone. All selected leads will be updated simultaneously. Changes will be logged in the system.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t bg-gray-50/80 flex items-center gap-3">
                    <Button
                        onClick={handleBulkUpdate}
                        disabled={loading || getActionSummary().length === 0}
                        className={`flex-1 h-10 text-sm font-medium rounded-lg shadow-sm ${
                            bulkActions.deleteLeads
                                ? "bg-red-600 hover:bg-red-700 text-white"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                        }`}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {bulkActions.deleteLeads ? "Deleting..." : "Updating..."}
                            </>
                        ) : (
                            <>
                                {bulkActions.deleteLeads ? (
                                    <>
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete {selectedLeads.length} Leads
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Apply Changes
                                    </>
                                )}
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                        className="h-10 px-5 text-sm font-medium rounded-lg"
                    >
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
